import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, TrendUp, TrendDown, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import ImportDialog from "@/components/ImportDialog";
import { exportToExcel, exportToPDF } from "@/lib/export";

const KATEGORI_PEMASUKAN = ["penjualan_udang", "lainnya"];
const KATEGORI_PENGELUARAN = ["pembelian_pakan", "pembelian_probiotik", "gaji", "operasional", "listrik_air", "perbaikan", "lainnya"];
const EXPORT_COLS = [
  { header: "Tanggal", accessor: (r) => r.tanggal },
  { header: "Tipe", accessor: (r) => r.tipe },
  { header: "Kategori", accessor: (r) => r.kategori?.replace(/_/g, " ") },
  { header: "Jumlah", accessor: (r) => r.jumlah },
  { header: "Keterangan", accessor: (r) => r.keterangan || "-" },
  { header: "Dibuat Oleh", accessor: (r) => r.created_by || "-" },
];

export default function KeuanganPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ tipe: "pemasukan", kategori: "penjualan_udang", jumlah: 0, tanggal: "", keterangan: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/keuangan?page=${page}&limit=15&search=${search}${filter ? `&tipe=${filter}` : ""}`);
      setItems(data.data);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search, filter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/keuangan", form);
      toast.success("Transaksi berhasil dicatat");
      setDialogOpen(false);
      setForm({ tipe: "pemasukan", kategori: "penjualan_udang", jumlah: 0, tanggal: "", keterangan: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);
  const totalPemasukan = items.filter(i => i.tipe === "pemasukan").reduce((a, b) => a + (b.jumlah || 0), 0);
  const totalPengeluaran = items.filter(i => i.tipe === "pengeluaran").reduce((a, b) => a + (b.jumlah || 0), 0);

  return (
    <div data-testid="keuangan-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Keuangan</h1>
          <p className="text-sm text-slate-500 mt-1">Pembukuan keuangan koperasi P3UW</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ImportDialog onSuccess={load} />
          <ExportButtons onExcel={() => exportToExcel(items, EXPORT_COLS, "keuangan")} onPDF={() => exportToPDF(items, EXPORT_COLS, "keuangan", "Laporan Keuangan P3UW")} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-keuangan-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><Plus size={18} className="mr-2" /> Catat Transaksi</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Transaksi Keuangan Baru</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tipe</Label>
                    <Select value={form.tipe} onValueChange={v => setForm({ ...form, tipe: v, kategori: v === "pemasukan" ? "penjualan_udang" : "pembelian_pakan" })}>
                      <SelectTrigger data-testid="select-tipe-keuangan"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pemasukan">Pemasukan</SelectItem><SelectItem value="pengeluaran">Pengeluaran</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kategori</Label>
                    <Select value={form.kategori} onValueChange={v => setForm({ ...form, kategori: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{(form.tipe === "pemasukan" ? KATEGORI_PEMASUKAN : KATEGORI_PENGELUARAN).map(k => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah (Rp)</Label><Input data-testid="input-jumlah-keuangan" type="number" value={form.jumlah} onChange={e => setForm({ ...form, jumlah: parseFloat(e.target.value) || 0 })} required /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal</Label><Input data-testid="input-tanggal-keuangan" type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} required /></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Keterangan</Label><Input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} /></div>
                <Button type="submit" data-testid="submit-keuangan-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-emerald-50 border border-emerald-200"><CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1"><TrendUp size={16} weight="bold" className="text-emerald-600" /><p className="text-xs uppercase tracking-[0.1em] font-semibold text-emerald-600">Pemasukan</p></div>
          <p className="text-2xl font-semibold text-emerald-800" style={{ fontFamily: "Work Sans" }}>{formatCurrency(totalPemasukan)}</p>
        </CardContent></Card>
        <Card className="bg-red-50 border border-red-200"><CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1"><TrendDown size={16} weight="bold" className="text-red-600" /><p className="text-xs uppercase tracking-[0.1em] font-semibold text-red-600">Pengeluaran</p></div>
          <p className="text-2xl font-semibold text-red-800" style={{ fontFamily: "Work Sans" }}>{formatCurrency(totalPengeluaran)}</p>
        </CardContent></Card>
        <Card className="bg-[#B8860B]/5 border border-[#B8860B]/20"><CardContent className="p-5">
          <p className="text-xs uppercase tracking-[0.1em] font-semibold text-[#B8860B]">Saldo (Halaman)</p>
          <p className="text-2xl font-semibold text-[#B8860B] mt-1" style={{ fontFamily: "Work Sans" }}>{formatCurrency(totalPemasukan - totalPengeluaran)}</p>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-keuangan" placeholder="Cari transaksi..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
        </div>
        <div className="flex gap-2">
          <Button variant={filter === "" ? "default" : "outline"} size="sm" onClick={() => { setFilter(""); setPage(1); }} className={filter === "" ? "bg-[#B8860B] text-white" : ""}>Semua</Button>
          <Button variant={filter === "pemasukan" ? "default" : "outline"} size="sm" onClick={() => { setFilter("pemasukan"); setPage(1); }} className={filter === "pemasukan" ? "bg-emerald-600 text-white" : ""}>Pemasukan</Button>
          <Button variant={filter === "pengeluaran" ? "default" : "outline"} size="sm" onClick={() => { setFilter("pengeluaran"); setPage(1); }} className={filter === "pengeluaran" ? "bg-red-600 text-white" : ""}>Pengeluaran</Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Tanggal</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Tipe</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Kategori</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Jumlah</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Keterangan</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Dibuat Oleh</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada transaksi</TableCell></TableRow>
              ) : items.map(i => (
                <TableRow key={i._id} className="hover:bg-slate-50">
                  <TableCell className="text-sm">{i.tanggal}</TableCell>
                  <TableCell>
                    <Badge className={i.tipe === "pemasukan" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                      {i.tipe === "pemasukan" ? "Pemasukan" : "Pengeluaran"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{i.kategori?.replace(/_/g, " ")}</TableCell>
                  <TableCell className={`text-sm font-semibold ${i.tipe === "pemasukan" ? "text-emerald-700" : "text-red-700"}`}>{i.tipe === "pemasukan" ? "+" : "-"}{formatCurrency(i.jumlah)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{i.keterangan || "-"}</TableCell>
                  <TableCell className="text-sm">{i.created_by || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
        </CardContent>
      </Card>
    </div>
  );
}
